import type { AssistantRingView, UserEventView } from '@crowdex/agent-contracts/api';
import { Phone, PhoneIncoming, PhoneOff } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { onStreamedEvent } from '@/features/notifications/notification-feed';
import { RING_EVENT, useShowInPage } from './assistant-show';
import { answerRing, loadRing } from './call-api';
import { useLiveCallContext } from './live-call';
import { useOpenLive } from './live-panel';
import type { LiveCall } from './use-live-call';

/** What the service worker tells a page when the user answers from the system notification. */
const ANSWER_MESSAGE = 'crowdex:answer-call';
/** The page opened by answering from a notification, no page being open: `/?answer-call=<ring>`. */
const ANSWER_PARAM = 'answer-call';

/** How a ring's event changes the ring shown: rings, or stops ringing (answered, declined…). */
export function ringChange(
  event: Pick<UserEventView, 'type' | 'body' | 'fields'>,
): { ringing: AssistantRingView } | { stopped: string } | null {
  if (event.type !== RING_EVENT) return null;
  const { ring, state, until, conversation, file, assistant, persona } = event.fields;
  if (typeof ring !== 'string') return null;
  if (state !== 'ringing') return { stopped: ring };
  if (typeof until !== 'string') return null;
  return {
    ringing: {
      ring,
      reason: event.body ?? '',
      // What the call tells beyond the reason comes when it is answered.
      details: null,
      file: typeof file === 'string' ? file : null,
      conversation: typeof conversation === 'string' ? conversation : null,
      assistant: typeof assistant === 'string' ? assistant : null,
      persona: typeof persona === 'string' ? persona : null,
      until,
    },
  };
}

/** The ring answered from a system notification, when this page was opened for it. */
function answeredFromUrl(): string | null {
  const url = new URL(window.location.href);
  const ring = url.searchParams.get(ANSWER_PARAM);
  if (ring === null) return null;
  url.searchParams.delete(ANSWER_PARAM);
  window.history.replaceState(window.history.state, '', url);
  return ring;
}

/**
 * The assistant's ring going on: the one going on when the page opened, then as its events say,
 * until it stops ringing.
 */
function useRing(): [AssistantRingView | null, () => void] {
  const [ring, setRing] = useState<AssistantRingView | null>(null);
  useEffect(() => {
    let cancelled = false;
    void loadRing().then(
      (current) => {
        if (!cancelled && current !== null) setRing(current);
      },
      () => undefined,
    );
    const stop = onStreamedEvent((event) => {
      const change = ringChange(event);
      if (change === null) return;
      if ('ringing' in change) setRing(change.ringing);
      else setRing((current) => (current?.ring === change.stopped ? null : current));
    });
    return () => {
      cancelled = true;
      stop();
    };
  }, []);
  // Missed once it rang its time.
  useEffect(() => {
    if (ring === null) return;
    const timer = setTimeout(
      () => setRing((current) => (current === ring ? null : current)),
      Math.max(0, Date.parse(ring.until) - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [ring]);
  return [ring, useCallback(() => setRing(null), [])];
}

/** A soft two-tone ring every few seconds while the card shows; silent where audio is refused. */
function useRingtone(on: boolean): void {
  useEffect(() => {
    if (!on || typeof AudioContext === 'undefined') return;
    const context = new AudioContext();
    const ring = () => {
      void context.resume().catch(() => undefined);
      for (const [index, frequency] of [880, 660].entries()) {
        const at = context.currentTime + index * 0.35;
        const tone = context.createOscillator();
        const gain = context.createGain();
        tone.frequency.value = frequency;
        gain.gain.setValueAtTime(0, at);
        gain.gain.linearRampToValueAtTime(0.08, at + 0.03);
        gain.gain.linearRampToValueAtTime(0, at + 0.3);
        tone.connect(gain).connect(context.destination);
        tone.start(at);
        tone.stop(at + 0.32);
      }
      navigator.vibrate?.([300, 200, 300]);
    };
    ring();
    const timer = setInterval(ring, 3_000);
    return () => {
      clearInterval(timer);
      void context.close().catch(() => undefined);
    };
  }, [on]);
}

/** Mounts the incoming call once, in the shell, where Live is available. */
export function IncomingCallHost() {
  const context = useLiveCallContext();
  if (context === null || !context.enabled) return null;
  return <IncomingCall call={context.call} />;
}

/**
 * The assistant calling the user (`call_user`, or an event they chose to be called about): a card
 * that rings over the app until they answer (the call starts and says why first), decline, or
 * another page answers. Answering from the system notification answers here.
 */
function IncomingCall({ call }: { call: LiveCall }) {
  const [ring, clear] = useRing();
  const [answering, setAnswering] = useState(false);
  const openLive = useOpenLive();
  const show = useShowInPage();
  const idle = call.status === 'idle';
  const latest = useRef({ ring, idle, openLive, show, start: call.start });
  latest.current = { ring, idle, openLive, show, start: call.start };

  const answer = useCallback(async (id: string) => {
    if (!latest.current.idle) return;
    setAnswering(true);
    try {
      await latest.current.openLive().catch(() => undefined);
      const answered = await latest.current.start({ ring: id });
      // The result it called to show, beside the call.
      if (answered?.status === 'answered' && answered.file && answered.conversation)
        await latest.current
          .show({ conversation: answered.conversation, file: answered.file })
          .catch(() => undefined);
    } finally {
      setAnswering(false);
    }
  }, []);

  const decline = useCallback(
    (id: string) => {
      clear();
      void answerRing(id, false).catch(() => undefined);
    },
    [clear],
  );

  // Answered from the system notification: in this page, or the one it opened.
  useEffect(() => {
    const fromUrl = answeredFromUrl();
    if (fromUrl !== null) void answer(fromUrl);
    const worker = navigator.serviceWorker;
    if (worker === undefined) return;
    const listener = (message: MessageEvent) => {
      const data = message.data as { type?: unknown; ring?: unknown } | null;
      if (data?.type === ANSWER_MESSAGE && typeof data.ring === 'string') void answer(data.ring);
    };
    worker.addEventListener('message', listener);
    return () => worker.removeEventListener('message', listener);
  }, [answer]);

  const shown = ring !== null && idle;
  useRingtone(shown && !answering);
  if (!shown) return null;
  return (
    <section
      aria-label="Your assistant calls you"
      aria-live="assertive"
      className="fixed top-4 left-1/2 z-[60] flex w-96 max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center gap-3 rounded-2xl border bg-popover p-3 text-popover-foreground shadow-lg"
      role="alertdialog"
    >
      <span className="flex size-11 shrink-0 animate-pulse items-center justify-center rounded-full bg-accent text-foreground">
        <PhoneIncoming aria-hidden="true" className="size-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-sm">Your assistant calls you</p>
        {ring.reason && <p className="line-clamp-2 text-muted-foreground text-xs">{ring.reason}</p>}
      </div>
      <button
        aria-label="Decline"
        className="flex size-11 shrink-0 items-center justify-center rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        disabled={answering}
        onClick={() => decline(ring.ring)}
        title="Decline"
        type="button"
      >
        <PhoneOff aria-hidden="true" className="size-5" />
      </button>
      <button
        aria-label="Answer"
        className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        disabled={answering}
        onClick={() => void answer(ring.ring)}
        title="Answer"
        type="button"
      >
        <Phone aria-hidden="true" className="size-5" />
      </button>
    </section>
  );
}
